<template>
  <validation-observer v-slot="{ invalid }">
    <form @submit.prevent="publish">
      <modal
        id="publish-version"
        :size="`lg`"
        :confirm-text="`Publish`"
        confirm-class="btn-primary"
        :confirm-disabled="invalid || versionPublished || isWarning"
        @confirm="publish"
        :close-text="'Cancel'"
        close-class="btn-default pull-left"
        :close-disabled="versionPublished"
        :showExit="true"
        :is-loading="loading"
      >
        <template v-slot:title>Publish</template>

        <template v-slot:body>
          <template v-if="loading">
            <loader></loader>
          </template>

          <template v-if="!loading">
            <p class="lead">Please complete the fields below to create your CapsimInbox.</p>
            <template v-for="(message, i) in validationMessageArr">
              <li :key="`${message}-${i}`">{{ message }}</li>
            </template>
            <validation-provider rules="minLength:4|required" v-slot="{ errors }">
              <base-input
                v-model="form.courseTitle"
                label="Course Title:"
                id="course-title"
                style="width: 40%"
                :isLargeFont="false"
                :error="errors[0]"
              />
            </validation-provider>

            <validation-provider rules="minValue:1|required" v-slot="{ errors }">
              <base-input
                v-model="form.numberOfStudents"
                label="Number of Participants:"
                id="participants"
                style="width: 40%"
                :isLargeFont="false"
                :error="errors[0]"
              />
            </validation-provider>

            <validation-provider name="startDate" rules="required" v-slot="{ errors }">
              <base-date-picker
                v-model="form.startDate"
                label="Start Date:"
                id="startDate"
                :isLargeFont="false"
                :error="errors[0]"
                :dateConfig="dateConfig"
              />
            </validation-provider>
            <validation-provider
              rules="isDateAfter:@startDate|dateExpiration:6|required"
              v-slot="{ errors }"
            >
              <base-date-picker
                v-model="form.endDate"
                label="End Date:"
                id="endDate"
                :isLargeFont="false"
                :error="errors[0]"
                :dateConfig="dateConfig"
              />
            </validation-provider>
            <hr />

            <validation-provider rules="minValue:10|required" v-slot="{ errors }">
              <div class="form-group" :class="{ 'has-error': errors[0] }">
                <label for="price">Price:</label>
                <div class="flex">
                  <div class="input-group col-md-3 col-lg-2 col-sm-3 col-xs-4">
                    <div class="input-group-addon">$</div>
                    <input
                      id="price"
                      v-model.number="form.price"
                      type="text"
                      class="form-control"
                    />
                  </div>
                  <div class="price-message mt-5 ml-20">
                    Price must be set to a minimum $10 per user.
                  </div>
                </div>
              </div>
            </validation-provider>

            <template v-if="versionPublished">
              <p class="publish-success p-20">
                Your version was successfully published. Your Administrator dashboard should open in
                a new tab.
              </p>
            </template>
            <template v-if="isWarning">
              <div class="row">
                <div class="col-md-12 text-center">
                  <div class="alert alert-warning">
                    {{ errorMessage }}
                  </div>
                </div>
              </div>
            </template>
          </template>
        </template>
      </modal>
    </form>
  </validation-observer>
</template>

<script>
  import { mapActions, mapState } from 'vuex';
  import BaseEditorPage from '@authoringPlatform/shared/BaseEditorPage.vue';
  import { publishVersion } from '@authoringPlatform/../api/publish';

  export default {
    name: 'Publish',

    data() {
      return {
        loading: false,
        validationMessageArr: [],
        validationMessagePrice: null,
        form: {
          courseTitle: '',
          numberOfStudents: 1,
          startDate: '',
          endDate: '',
          price: 10,
        },
        dateConfig: {
          minDate: new Date(),
          format: 'MM/DD/YYYY',
        },
        versionPublished: false,
        isWarning: false,
        errorMessage: '',
      };
    },

    props: {
      versionKey: {
        type: Number,
        required: true,
        default: 0,
      },
      fromRecentProject: {
        type: Boolean,
        required: false,
        default: false,
      },
    },

    components: {
      BaseEditorPage,
    },

    watch: {
      versionKey(oldKey, newKey) {
        if (oldKey !== newKey) {
          this.resetForm();
        }
      },
    },
    computed: {
      ...mapState('user', ['actorKey']),
    },

    methods: {
      ...mapActions(['notify']),
      ...mapActions('versions', ['getAllRecentProjects']),
      resetForm() {
        this.isWarning = false;
        this.errorMessage = '';
        const resetForm = {
          courseTitle: '',
          numberOfStudents: 1,
          startDate: '',
          endDate: '',
          price: 10,
        };

        this.form = { ...resetForm };
      },
      async publish() {
        try {
          this.loading = true;
          const response = await publishVersion({
            ...this.form,
            FK_versionKey: this.versionKey,
          });

          if (response.url !== undefined) window.open(response.url, '_blank');
          this.loading = false;
          if (response.notify.isWarning == false) {
            this.versionPublished = true;
          }

          if (response.notify.isWarning) {
            this.errorMessage = response.notify.message;
            this.isWarning = response.notify.isWarning;
          }

          if (this.fromRecentProject) {
            await this.getAllRecentProjects({ authorKey: this.actorKey });
          }
        } catch (e) {
          this.notify({
            message: 'Error while creating your course – Please contact Capsim Support.',
            isWarning: true,
          });
        } finally {
          this.loading = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .loader-container {
    height: 50px;
  }
  .publish-success {
    background-color: #dff0d8;
  }
</style>
